import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import CateringProviderCard from "./carthelpercomponents/CateringProviderCard";
import PriestCard from "./carthelpercomponents/PriestCard";
import TentProviderCard from "./carthelpercomponents/TentProviderCard";
import VenueProviderCard from "./carthelpercomponents/VenueProviderCard";

import config from "../../config";

import {
  clearCheckoutRequest,
  fetchCheckoutPage,
  applyCoupon,
  removeCoupon,
} from "../../store/checkoutSlice";

import {
  payment_checkout,
  payment_verification,
} from "../../Apiservices/Auth/payments/orderevent";

import "./checkoutPage.css";
import OrderAgreementProofs from '../documents/OrderAgreementProofs';
import { requestDocumentAgreements } from '../documents/DocumentAgreementModal';

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [payLoading, setPayLoading] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');

  // Local state for coupon input
  const [couponInput, setCouponInput] = useState("");

  const { eventId } = location.state || {};

  // -------------------------------
  // REDIRECT IF NO EVENT
  // -------------------------------
  useEffect(() => {
    if (!eventId) {
      navigate("/dashboard");
    }
  }, [eventId, navigate]);

  // -------------------------------
  // REDUX STATE
  // -------------------------------
  const checkoutState = useSelector((state) => state.checkout) || {};

  const {
    checkOutPageResponse,
    loading,
    refreshLoading,
    checkOutRequest,
  } = checkoutState;

  // -------------------------------
  // INIT
  // -------------------------------
  useEffect(() => {
    if (!eventId) return;

    dispatch(clearCheckoutRequest(eventId));
    dispatch(fetchCheckoutPage());
  }, [eventId, dispatch]);

  // -------------------------------
  // SYNC COUPON
  // -------------------------------
  useEffect(() => {
    if (checkOutRequest?.coupon) {
      setCouponInput(checkOutRequest.coupon);
    }
  }, [checkOutRequest]);

  useEffect(() => {
    if (!checkOutRequest?.event_id) return;

    dispatch(fetchCheckoutPage());
  }, [
    checkOutRequest?.priest,
    checkOutRequest?.tent,
    checkOutRequest?.catering,
    checkOutRequest?.venue,
    checkOutRequest?.tent_pct,
    checkOutRequest?.priest_pct,
    checkOutRequest?.catering_pct,
    checkOutRequest?.venue_pct,
  ]);

  // -------------------------------
  // COUPON
  // -------------------------------
  const handleApplyCoupon = () => {
    dispatch(applyCoupon(couponInput));
    dispatch(fetchCheckoutPage());
  };

  const handleRemoveCoupon = () => {
    dispatch(removeCoupon());
    setCouponInput("");
    dispatch(fetchCheckoutPage());
  };

  // -------------------------------
  // PAYMENT PAYLOAD
  // -------------------------------
  const buildPaymentPayload = () => {
    if (!checkOutPageResponse) return {};

    const payload = {
      event_id: eventId,
      coupon_code: checkOutRequest?.coupon || "",
      amount: {},
    };

    const res = checkOutPageResponse;

    // Priest
    if (res.priestItems?.length) {
      payload.amount.priest = res.priestItems
        .filter((p) => p.paymentSummary?.payable_now > 0)
        .map((p) => ({
          priestId: p.priest?.id,
          amount: p.paymentSummary?.payable_now,
        }));
    }

    // Tent
    if (res.tent_providers?.length) {
      payload.amount.tentRentals = res.tent_providers
        .filter((t) => t.paymentSummary?.payable_now > 0)
        .map((t) => ({
          tentRentalId: t.provider_id,
          amount: t.paymentSummary?.payable_now,
        }));
    }

    // Catering
    if (res.catering_providers?.length) {
      payload.amount.catering = res.catering_providers
        .filter((c) => c.paymentSummary?.payable_now > 0)
        .map((c) => ({
          cateringId: c.provider_id,
          amount: c.paymentSummary?.payable_now,
        }));
    }

    // Venue
    if (res.venue_items?.length) {
      payload.amount.venue = res.venue_items
        .filter((v) => v.paymentSummary?.payable_now > 0)
        .map((v) => ({
          venueProviderId: v.providerId,
          amount: v.paymentSummary?.payable_now,
        }));
    }

    return payload;
  };

  // -------------------------------
  // PAYMENT
  // -------------------------------
  const handlePayment = async () => {
    try {
      setPayLoading(true);

      const payload = buildPaymentPayload();

      const checkoutData = await payment_checkout(eventId, payload);

      const options = {
        key: checkoutData.key_id,
        amount: checkoutData.order.amount,
        currency: "INR",
        order_id: checkoutData.order.id,
        name: "Event Booking",
        description: checkOutPageResponse?.eventTitle || "Payment",
        handler: async function (response) {
          await payment_verification(eventId, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          alert("Payment Successful 🎉");
          navigate("/dashboard");
        },
        prefill: {
          name: "User",
          email: "test@mail.com",
          contact: "9999999999",
        },
        theme: { color: "#0D47A1" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      if (err.cancelled) return;
      console.error(err);
      alert("Payment failed");
    } finally {
      setPayLoading(false);
    }
  };

  // -------------------------------
  // HELPER FUNCTIONS
  // -------------------------------
  const formatDate = (dateStr) => {
    if (!dateStr) return "Invalid date";
    const dt = new Date(dateStr);
    if (isNaN(dt)) return "Invalid date";

    const day = dt.toLocaleDateString("en-IN", { weekday: "short" });
    const date = dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    const time = dt.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${day}, ${date} | ${time}`;
  };

  const formatCurrency = (amount) => {
    if (amount == null) return "₹0";
    return `₹${Math.round(amount).toLocaleString()}`;
  };

  // -------------------------------
  // UI STATES
  // -------------------------------
  if (loading) return <div className="loader">Loading checkout...</div>;

  if (!checkOutPageResponse) return <div>No checkout data found</div>;

  const response = checkOutPageResponse;

  // Check if there are any services to display
  const hasServices = 
    response.priestItems?.length > 0 ||
    response.tent_providers?.length > 0 ||
    response.catering_providers?.length > 0 ||
    response.venue_items?.length > 0;

  if (!hasServices) {
    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <button onClick={() => navigate(-1)}>← Back</button>
          <h2>Checkout</h2>
        </div>
        <div className="empty-state">
          <p>No services in your cart to checkout.</p>
          <button onClick={() => navigate("/dashboard")}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <div className="checkout-container">
      {/* HEADER */}
      <div className="checkout-header">
        <button onClick={() => navigate(-1)}>← Back</button>
        <h2>Checkout</h2>
        <OrderAgreementProofs eventId={eventId} />
        <button type="button" disabled={payLoading || reviewBusy} onClick={async () => {
          setReviewBusy(true); setReviewError('');
          try {
            const payload = buildPaymentPayload();
            await requestDocumentAgreements({ eventId, amount: payload.amount }, { beforeCheckout: true, showCompleted: true });
          } catch (error) { setReviewError(error.response?.data?.message || error.message || 'Unable to load contracts. Please retry.'); }
          finally { setReviewBusy(false); }
        }}>{reviewBusy ? 'Loading contracts…' : 'Review contracts'}</button>
        {reviewError && <p role="alert">{reviewError}</p>}
      </div>

      {/* EVENT CARD */}
      <div className="event-card">
        <img
          className="event-image"
          src={
            response?.event_image_id
              ? `${config.API_BASE_URL}/gallery/download/${response.event_image_id}?isDownload=false`
              : "/assets/images/thurent-favicon.svg"
          }
          alt={response?.event_title || "event"}
          onError={(e) => {
            e.target.src = "/assets/images/thurent-favicon.svg";
          }}
        />

        <div>
          <h4>{response.event_title}</h4>
          <p>{response.venue}</p>
          <p>{formatDate(response.event_start_date)}</p>
        </div>
      </div>

      {/* VENUE SERVICES */}
      {response.venue_items?.length > 0 && (
        <div className="service-section">
          <h3>Venue Services</h3>
          {response.venue_items.map((item) => (
            <VenueProviderCard
              key={item.id}
              item={item}
              eventId={eventId}
              isCheckout={true}
            />
          ))}
        </div>
      )}

      {/* CATERING SERVICES */}
      {response.catering_providers?.length > 0 && (
        <div className="service-section">
          <h3>Catering Services</h3>
          {response.catering_providers.map((provider) => (
            <CateringProviderCard
              key={provider.provider_id}
              provider={provider}
              eventId={eventId}
              isCheckout={true}
            />
          ))}
        </div>
      )}

      {/* PRIEST SERVICES */}
      {response.priestItems?.length > 0 && (
        <div className="service-section">
          <h3>Priest Services</h3>
          {response.priestItems.map((provider) => (
            <PriestCard
              key={provider.priest?.id}
              item={provider}
              eventId={eventId}
              isCheckout={true}
              isOrders={false}
            />
          ))}
        </div>
      )}

      {/* TENT SERVICES */}
      {response.tent_providers?.length > 0 && (
        <div className="service-section">
          <h3>Tent House Rentals</h3>
          {response.tent_providers.map((p) => (
            <TentProviderCard
              key={p.provider_id}
              provider={p}
              eventId={eventId}
              isCheckout={true}
              isOrders={false}
            />
          ))}
        </div>
      )}

      {/* COUPON */}
      <div className="coupon-box">
        <div className="row-between">
          <input
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            placeholder="Enter coupon"
          />
          <button onClick={handleApplyCoupon}>Apply</button>
        </div>

        {response.coupon && (
          <div className="coupon-status">
            {response.coupon.valid ? (
              <span className="coupon-valid">✓ Coupon Applied</span>
            ) : (
              <span className="coupon-invalid">{response.coupon.message}</span>
            )}
            <button onClick={handleRemoveCoupon}>Remove</button>
          </div>
        )}
      </div>

      {/* PAYMENT SUMMARY */}
      <div className="summary-box">
        <h3>Payment Summary</h3>
        <div className="row-between">
          <span className="row-title">Total</span>
          <span className="row-value">{formatCurrency(response.total)}</span>
        </div>
        <div className="row-between">
          <span className="row-title">Paid</span>
          <span className="row-value">{formatCurrency(response.paid)}</span>
        </div>
        <div className="row-between">
          <span className="row-title">Balance</span>
          <span className="row-value">{formatCurrency(response.balanceToPay)}</span>
        </div>
        {response.coupon && (
          <div className="row-between">
            <span className="row-title">Coupon Discount</span>
            <span className="row-value">-{formatCurrency(response.newCouponDiscount || 0)}</span>
          </div>
        )}
        {response.payLaterBalance > 0 && (
          <div className="row-between">
            <span className="row-title">Pay Later Balance</span>
            <span className="row-value">{formatCurrency(response.payLaterBalance)}</span>
          </div>
        )}
      </div>

      {/* PAYMENT BREAKDOWN */}
      <div className="summary-box">
        <h3>Payment Breakdown</h3>
        {response.logs?.map((log, index) => (
          <div key={index} className="row-between">
            <span className="row-title">{log.title}</span>
            <span className="row-value">{log.body}</span>
          </div>
        ))}
      </div>

      {/* BOTTOM PAYMENT BAR */}
      <div className="bottom-bar">
        <div className="payable-amount">
          <span className="label">Payable Now</span>
          <h3>{formatCurrency(response.totalPayableNow)}</h3>
        </div>

        <button
          disabled={(response.totalPayableNow || 0) <= 0 || payLoading || reviewBusy}
          onClick={handlePayment}
          className="pay-button"
        >
          {payLoading ? (
            <span className="loading-text">Processing...</span>
          ) : (
            `Pay ${formatCurrency(response.totalPayableNow)}`
          )}
        </button>
      </div>

      {/* LOADING OVERLAY */}
      {refreshLoading && <div className="overlay-loader">Updating...</div>}
    </div>
  );
};

export default CheckoutPage;
